import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  useTheme,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const accreditations = [
  {
    title: 'CASLE',
    logo: '/images/castle.jfif',
    description: 'CASLE was formed in 1969 as a federation of independent professional societies representing surveying and land economy in Commonwealth countries. Geomaps Africa Ltd is an associated and accredited partner.',
  },
  {
    title: 'ISO 9001:2015',
    logo: '/images/iso.png',
    description: 'ISO 9001 is defined as the international standard that specifies requirements for a quality management system (QMS). Organizations use the standard to demonstrate the ability to consistently provide products and services that meet customer and regulatory requirements. Geomaps Africa Ltd is ISO 9001 Certified.',
  },
  {
    title: 'Kenya Institute of Planners',
    logo: '/images/kip.jfif',
    description: 'The institute seeks to make Kenya a planning society by enhancing collaboration with governmental and Non-Governmental agencies, as well as sustainable national, regional and local development planning. It seeks to maintain oversight on the practice and conduct of members of the legitimate profession of planning. Geomaps Africa Ltd is an associated and accredited partner.',
  },
  {
    title: 'ICT Authority',
    logo: '/images/ict.png',
    description: 'The Information and Communication Technology (ICT) Authority is a State Corporation under the Ministry of Information Communication and Technology. The corporation was established in August 2013. The Authority is tasked with rationalizing and streamlining the management of all Government of Kenya ICT functions. Geomaps Africa Ltd is an associated and accredited partner.',
  },
  {
    title: 'Land Surveyors Board',
    logo: '/images/lsb.jfif',
    description: 'The Land Surveyors Board was founded in 1923 and operates under the authority of the Survey Act Cap 299 of the Laws of Kenya. We regulate land surveyors\' practice to ensure professionalism and uphold industry standards within the Republic of Kenya. Geomaps Africa Ltd is an associated and accredited partner.',
  },
  {
    title: 'Institution of Surveyors of Kenya',
    logo: '/images/isk.jfif',
    description: 'The Institution of Surveyors of Kenya (ISK) is a land sector professionals\' organization made up of Valuers, Land Surveyors, Geomatic Engineers, Registered Estate Agents, Property Managers, Building Surveyors, Land Administration Managers and Facilities Managers. Geomaps Africa Ltd is an associated and accredited partner.',
  },
];

const achievements = [
  {
    year: '2025',
    title: 'AI Meets GIS: Unlocking the Future of Geospatial Intelligence',
    description: 'The integration of Artificial Intelligence (AI) in geospatial innovation is revolutionizing how spatial data is analyzed, interpreted, and applied. By combining AI with GIS technologies, Geomaps automates complex analyses, uncover hidden patterns, and generate predictive insights that drive smarter planning, resource management, and decision-making across sectors',
  },
  {
    year: 'Since 2023',
    title: 'Digitizing Land and Modernizing Service Delivery for County Governments: The GeoManager GIS Revolution',
    description: 'The implementation of Geomanager GIS across County Governments represents a significant step toward enhancing spatial data management and service delivery. This integrated platform empowers counties to digitize land records, streamline planning processes, and improve decision-making through accurate, real-time geospatial intelligence.',
  },
  {
    year: '2023',
    title: 'Leveraging Geospatial Technologies for Land Tenure Security in Zambezia Province',
    description: 'Phase IIA of the MozLand (TERRA SEGURA) Project focused on the delimitation of communities, basic land use planning, land regularization, parcel demarcation, boundary surveys, and the preparation and issuance of DUATs (Land Use Rights). This phase successfully covered over 165,000 individual land parcels and 47 community-delimited parcels across seven districts in Zambezia Province. The initiative aimed to enhance land tenure security, support sustainable land management, and empower local communities through improved access to formal land rights',
  },
  {
    year: 'Since 2021',
    title: 'Mapping Value: The First GIS-Driven Mass Valuation Roll in Kenya',
    description: 'This marks the first-ever implementation of a GIS-based Mass Valuation Roll in Kenya, setting a new benchmark in property valuation by integrating spatial technology with large-scale assessment. The project pioneers the use of geospatial data to streamline and standardize property valuation across jurisdictions, enhancing transparency, accuracy, and efficiency.',
  },
  {
    year: '2020',
    title: 'Empowering Clean Energy Through Geospatial Innovation: Bahari Wind 90MW Project',
    description: 'The Bahari Wind 90MW Power Project involved comprehensive geospatial and survey services to support its planning, detailed engineering design and implementation phases. Key components included aerial topographic and LiDAR mapping, enabling accurate terrain modeling and spatial analysis for infrastructure design. In parallel, physical planning and cadastral surveys were conducted to guide land use and project layout.As part of the Resettlement Action Plan (RAP), detailed cadastral survey works were carried out for the resettlement area, covering over 3,000 individual plots. These activities ensured secure land tenure, facilitated community relocation, and supported regulatory compliance—demonstrating the critical role of geospatial technologies and survey expertise in large-scale renewable energy development.',
  },
  {
    year: 'Since 2015',
    title: 'Revolutionizing Geospatial Mapping: Elevating Accuracy with Next-Gen Integrated LiDAR and Large Format Aerial Imaging in a Single Flight',
    description: 'The integration of large format camera and LiDAR scanner into a unified platform marked a significant advancement in geospatial data acquisition for Geomaps. This has enabled simultaneous acquisition of high-resolution imagery and precise elevation data, delivering a comprehensive geospatial solutions.',
  },
];

const StatCard = ({ number, label, icon: Icon, delay }) => (
  <motion.div
    initial={{ scale: 0, opacity: 0 }}
    whileInView={{ scale: 1, opacity: 1 }}
    viewport={{ once: true }}
    transition={{
      type: "spring",
      stiffness: 100,
      damping: 15,
      delay: delay,
    }}
  >
    <Box
      sx={{
        textAlign: 'center',
        p: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 2,
        backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
      }}
    >
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{ delay: delay + 0.3, duration: 0.5 }}
      >
        <Icon sx={{ fontSize: 40, mb: 2, color: 'rgba(255, 255, 255, 0.9)' }} />
      </motion.div>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ delay: delay + 0.5, duration: 0.5 }}
      >
        <Typography 
          variant="h2" 
          sx={{ 
            mb: 1, 
            fontWeight: 700,
            background: 'linear-gradient(45deg, #fff, #f0f0f0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {number}
        </Typography>
      </motion.div>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ delay: delay + 0.7, duration: 0.5 }}
      >
        <Typography 
          variant="h6"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
      </motion.div>
    </Box>
  </motion.div>
);

const AccreditationCard = ({ item, index }) => {
  const theme = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'visible',
          '&:hover': {
            transform: 'translateY(-8px)',
            transition: 'transform 0.3s ease-in-out',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            backgroundColor: 'background.paper',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={item.logo}
            alt={item.title}
            sx={{
              width: '180px',
              height: '180px',
              objectFit: 'contain',
              mb: 3,
            }}
          />
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 600,
              textAlign: 'center',
              color: theme.palette.primary.main,
            }}
          >
            {item.title}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              textAlign: 'justify',
              lineHeight: 1.6,
            }}
          >
            {item.description}
          </Typography>
        </Box>
      </Card>
    </motion.div>
  );
};

const AchievementCard = ({ achievement, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
  >
    <Paper
      elevation={0}
      sx={{
        p: 3,
        backgroundColor: 'rgba(0, 128, 0, 0.05)',
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <StarIcon sx={{ color: 'primary.main', mr: 1 }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'primary.main',
          }}
        >
          {achievement.year}
        </Typography>
      </Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        {achievement.title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {achievement.description}
      </Typography>
    </Paper>
  </motion.div>
);

const Awards = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          paddingTop: { xs: '160px', md: '180px' },
          paddingBottom: '60px',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/awards.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1,
          },
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              color="white"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Awards & Accreditations
            </Typography>
            <Typography
              variant="h5"
              color="white"
              sx={{
                maxWidth: '800px',
                opacity: 0.9,
              }}
            >
              Recognition of our commitment to excellence and industry standards
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Enhanced Stats Section */}
      <Box
        sx={{
          py: 8,
          backgroundColor: 'primary.main',
          background: 'linear-gradient(135deg, #1a5f7a 0%, #2e7d32 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={4}>
              <StatCard
                number="43+"
                label="Years of Excellence"
                icon={EmojiEventsIcon}
                delay={0}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                number="6"
                label="Major Accreditations"
                icon={VerifiedUserIcon}
                delay={0.2}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                number="100%"
                label="Quality Assurance"
                icon={CheckCircleIcon}
                delay={0.4}
              />
            </Grid>
          </Grid>
        </Container>
        {/* Add animated background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            zIndex: 0,
          }}
        >
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                width: Math.random() * 100 + 50,
                height: Math.random() * 100 + 50,
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
              animate={{
                y: [0, -100],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: Math.random() * 5 + 5,
                repeat: Infinity,
                delay: Math.random() * 5,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Accreditations Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f8f9fa' }}>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 700,
                mb: 2,
                textAlign: 'center',
              }}
            >
              Professional Accreditations
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                mb: 8,
                textAlign: 'center',
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              We maintain the highest standards through prestigious industry accreditations
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {accreditations.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <AccreditationCard item={item} index={index} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Recent Achievements Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.paper' }}>
        <Container>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
            <Typography
              variant="h2"
                    sx={{ 
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 700,
                mb: 1,
                textAlign: 'center',
              }}
            >
              Recent Achievements
                    </Typography>
                    <Typography 
              variant="h6"
              sx={{
                color: 'text.secondary',
                mb: 8,
                textAlign: 'center',
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              Recognition of our continuous commitment to excellence
                    </Typography>
                </motion.div>

          <Grid container spacing={4}>
            {achievements.map((achievement, index) => (
              <Grid item xs={12} md={4} key={index}>
                <AchievementCard achievement={achievement} index={index} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Awards; 